<template>
  <FloatingBubble
    v-if="isVisible"
    v-model:offset="offset"
    class="preview-rain-bubble"
    :style="bubbleStyle"
    :gap="0"
    magnetic="x"
    axis="xy">
    <Transition name="warmup-tada" @after-leave="onAfterLeave" appear>
      <div v-if="innerVisible" class="preview-rain" @click="handleClick">
        <RayChase mode="half" particleType="none" class="ray-bg" />

        <div class="preview-content">
          <div class="countdown">
            <div class="countdown-label">{{ $t('倒计时') }}</div>
            <div class="countdown-digits">
              <template v-for="(ch, idx) in countdownChars" :key="idx">
                <span v-if="ch !== ':'" class="digit">
                  <span class="num-shadow">{{ ch }}</span>
                  <span class="num">{{ ch }}</span>
                </span>
                <span v-else class="colon">:</span>
              </template>
            </div>
          </div>
        </div>
        <div class="close-hit" @click.stop="handleClose">
          <div class="close-icon">
            <SvgIcon name="svg/close.svg" />
          </div>
        </div>
      </div>
    </Transition>
  </FloatingBubble>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue'
import { useWindowSize } from '@vueuse/core'
import { useRedPacketRain } from '@/composables/useRedPacketRain'
import { FloatingBubble } from '@/components/base/floating-bubble'
import { useFloatStack } from '@/composables/useFloatStack'
import { useToken } from '@/composables/useToken'
import RayChase from '@/components/RayChase.vue'
import SvgIcon from '@/components/SvgIcon.vue'
import { showLogin } from '@/utils/authDialog'
import { remToPxValue, pxToRemValue } from '@/utils'

const { displayCountdown, shouldShowWarmupFloat } = useRedPacketRain()
const { isLoggedIn } = useToken()
const countdownChars = computed(() => (displayCountdown.value || '00:00').split(''))
const isDismissed = ref(false)
const innerVisible = ref(false)

const BUBBLE_WIDTH_PX = 91
const BUBBLE_HEIGHT_PX = 116
const BUBBLE_STACK_GAP_PX = 14

const { width: windowWidth } = useWindowSize()
const { insertFloatBubble } = useFloatStack()

const isVisible = computed(() => shouldShowWarmupFloat.value && !isDismissed.value)
const toRuntimePx = (value: number) => remToPxValue(pxToRemValue(value))
const bubbleWidthPx = computed(() => {
  windowWidth.value
  return toRuntimePx(BUBBLE_WIDTH_PX)
})
const bubbleHeightPx = computed(() => {
  windowWidth.value
  return toRuntimePx(BUBBLE_HEIGHT_PX)
})
const bubbleStackSizePx = computed(() => bubbleHeightPx.value + toRuntimePx(BUBBLE_STACK_GAP_PX))
const bubbleStyle = computed(() => ({
  '--bubble-width': pxToRemValue(BUBBLE_WIDTH_PX),
  '--bubble-height': pxToRemValue(BUBBLE_HEIGHT_PX),
  zIndex: '35'
}))

const offsetY = insertFloatBubble({
  id: 'warmup-float',
  stackSize: bubbleStackSizePx,
  visible: isVisible
})

const offset = ref({
  x: Math.max(0, windowWidth.value - bubbleWidthPx.value),
  y: offsetY.value
})

const syncOffset = () => {
  offset.value.x = Math.max(0, windowWidth.value - bubbleWidthPx.value)
}

// resize后useFloatStack可能会主动更新offsetY 这里同步给offset再传给FloatingBubble
watch([windowWidth, bubbleWidthPx], syncOffset, { immediate: true })
watch(
  offsetY,
  (nextY) => {
    offset.value.y = nextY
  },
  { immediate: true }
)
// 拖动时同步写回 useFloatStack，保证 reStackAll 能覆盖到最新位置
watch(
  () => offset.value.y,
  (nextY) => {
    if (nextY !== offsetY.value) offsetY.value = nextY
  }
)

onMounted(() => {
  // 延迟显示以展示出场动画
  // setTimeout(() => {
  // }, 5000)
  innerVisible.value = true
})

const handleClick = () => {
  if (!isLoggedIn.value) {
    showLogin()
  }
}

const handleClose = () => {
  innerVisible.value = false
}

const onAfterLeave = () => {
  isDismissed.value = true
}
</script>

<style scoped lang="scss">
.preview-rain {
  width: var(--bubble-width);
  height: var(--bubble-height);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s;
  position: relative;
}

.ray-bg {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: 0;
  pointer-events: none;
  top: 10px;
}

.preview-content {
  position: relative;
  z-index: 1;
  height: 98px;
  align-self: stretch;
  background-image: url('@/assets/imgs/activity/rain/preview_bg.webp');
  background-size: 82px 87px;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

:global(.preview-rain-bubble) {
  z-index: 20;
  overflow: visible;
  background: transparent;
  width: var(--bubble-width);
  height: var(--bubble-height);
  .van-floating-bubble {
    width: var(--bubble-width);
    height: var(--bubble-height);
  }
}
.close-icon {
  width: 17px;
  height: 17px;
  border-radius: 50%;
  background-color: rgba(255, 51, 0, 0.9);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  color: #fff;
}

.close-hit {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 2px;
  transition: opacity 0.3s;
}

.countdown {
  font-size: 9px;
  font-weight: 500;
  color: #fff3a9;
  margin-bottom: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.countdown-label {
  line-height: 1;
  margin-bottom: 2px;
}

.countdown-digits {
  display: flex;
  align-items: center;
  gap: 1px;
}

.digit {
  width: 12px;
  height: 14.5px;
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  position: relative;

  .num {
    z-index: 2;
    background: linear-gradient(180deg, #fff8df 0%, #ffe897 100%);
    color: transparent;
    background-clip: text;
    position: relative;
  }
  .num-shadow {
    position: absolute;
    transform: translate(-50%, -50%);

    left: 50%;
    top: 50%;

    color: transparent;
    text-shadow: 0.2px 0.2px 0.6px rgba(0, 0, 0, 0.5);
    z-index: 1; /* 在背景装饰之上，但在渐变字之下 */
  }
  &::after {
    position: absolute;
    z-index: 1;
    content: '';
    width: 100%;
    height: 7px;
    background-color: rgba(0, 0, 0, 0.2);
    top: 0;
    left: 0;
    border-radius: 2px 2px 0 0;
  }
  &::before {
    position: absolute;
    z-index: 1;
    content: '';
    width: 100%;
    height: 7px;
    background-color: rgba(0, 0, 0, 0.2);
    bottom: 0;
    left: 0;
    border-radius: 0 0 2px 2px;
  }
}

.colon {
  font-size: 9px;
  line-height: 7px;
  color: #fff3a9;
  font-weight: 700;
}

/* Animations */
.warmup-tada-enter-active {
  animation: tada-enter 1s;
  .close-hit {
    opacity: 0;
  }
}
.warmup-tada-leave-active {
  animation: pop-leave 0.3s;
  .close-hit {
    :deep(svg) {
      // styled组件异常 Transition包裹的SvgIcon样式会丢
      width: 100%;
      height: 100%;
    }
  }
}

@keyframes tada-enter {
  0% {
    transform: scale(0);
  }
  40% {
    transform: scale(1.1);
  }
  50% {
    transform: scale(1) rotate(-3deg);
  }
  60% {
    transform: scale(1) rotate(3deg);
  }
  70% {
    transform: scale(1) rotate(-3deg);
  }
  80% {
    transform: scale(1) rotate(2deg);
  }
  100% {
    transform: scale(1) rotate(0);
  }
}

@keyframes pop-leave {
  0% {
    transform: scale(1);
  }
  100% {
    transform: scale(0.1);
  }
}
</style>
